import { Fix64 } from "../maths/fix64";
import { RARITY_IDS, Rarity } from "./balance";
import { NO_OFFER_TRIGGERS, OfferTriggerRules, parseOfferTriggers } from "./offerTriggers";

/**
 * The Deals screen's catalog (docs/SHOP.md; balance/src/offers.json): real-money offers on a strip of pages, the
 * random chests some of them hold (odds shown), the value baseline and the VIP Special Packs (VIP levels themselves are
 * balance/src/vip.json, data/vip.ts). Parsed from the generated bundle's `offers` section; the rules that read it are
 * meta/{offerValue,deals}.ts. An older bundle has no section:
 * `emptyOffers` sells nothing.
 */

/** One thing an offer pays. `count` is how many of it (1 for gems and supplies, whose size is the amount). */
export type OfferItem =
  | { readonly kind: "gems"; readonly amount: number; readonly count: 1 }
  /** `chests` average Win chests of gold at the player's highest cleared level (orders.ts chestShare). */
  | { readonly kind: "supplies"; readonly chests: Fix64; readonly count: 1 }
  | { readonly kind: "speedUp"; readonly minutes: number; readonly count: number }
  /** A gear chest of this rarity at the player's highest cleared level, gear included (random: odds shown). */
  | { readonly kind: "chest"; readonly rarity: Rarity; readonly count: number }
  /** A chest whose rarity is rolled from `OffersData.luckyChests[table]` (random: odds shown). */
  | { readonly kind: "luckyChest"; readonly table: string; readonly count: number }
  /** One gear item of this rarity; `pick`: the player chooses the slot before paying; `any`: rolled (random). */
  | { readonly kind: "gear"; readonly rarity: Rarity; readonly slot: "pick" | "any"; readonly count: number }
  | { readonly kind: "skin"; readonly skinId: string; readonly count: 1 };
export type OfferItemKind = OfferItem["kind"];

/** How a page's purchase limits reset: at 00:00 UTC, Monday 00:00 UTC, every second UTC week, never, or no limit. */
export const ROTATIONS = ["daily", "weekly", "fortnight", "once", "none"] as const;
export type Rotation = (typeof ROTATIONS)[number];

/** A price: the App Store product id and its US reference tier (StoreKit shows the storefront's own price). */
export interface OfferPrice {
  readonly product: string;
  /** "$4.99", as offers.json writes it. */
  readonly label: string;
  /** 499. */
  readonly cents: number;
}

export interface OfferDef {
  readonly id: string;
  readonly pageId: string;
  readonly name: string;
  readonly price: OfferPrice;
  /** Purchases per rotation period (per save for `once`). */
  readonly limit: number;
  readonly items: readonly OfferItem[];
  /** Pick slots (Custom Chest): each is filled from `choices` before paying. 0 = none. */
  readonly picks: number;
  readonly choices: readonly OfferItem[];
  /** Master's Collection: exactly one prize is chosen before paying. Empty = none. */
  readonly prizes: readonly OfferItem[];
  /** Weekly Deal tokens this offer adds to the week's track. */
  readonly weeklyTokens: number;
  /** The level whose clear makes it appear (Chapter packs); 0 = the page's unlock rule only. */
  readonly levelTrigger: number;
  /** VIP Special Pack (docs/SHOP.md §8.4): on sale from this VIP level (meta/vipLevels.ts); 0 = no VIP gate. */
  readonly vipLevel: number;
  /** MP-04: on sale only once this offer id has been bought (Field Commander after First Command); "" = no such gate. */
  readonly after: string;
  /** MP-05: the Welcome Back pack, on sale only in the window after a return (meta/offerTriggers.ts welcomeOnSale). */
  readonly welcomeBack: boolean;
  /** A card (MP-01/02, meta/cards.ts): `items` now, then `daily` once a UTC day for `days` days, claimed in the hub. */
  readonly card: CardDef | null;
  /** A growth fund (MP-03, meta/cards.ts): each milestone pays once its level is first cleared. */
  readonly fund: FundDef | null;
  /**
   * The builder this offer hires (docs/SHOP.md §18, owner 2026-10-19: builders are real money only): builder n, on sale
   * only while the player owns n − 1, granted through the profile with the offer's items (meta/builders.ts); 0 = none.
   */
  readonly builder: number;
}

/** A card's daily payout (MP-01/02): what each of its `days` UTC days pays when claimed. */
export interface CardDef { readonly days: number; readonly daily: readonly OfferItem[] }
export interface FundMilestoneDef { readonly level: number; readonly items: readonly OfferItem[] }
/** A growth fund (MP-03): milestones by campaign level, rising. */
export interface FundDef { readonly milestones: readonly FundMilestoneDef[] }

/** The cards' shared rules (offers.json `cardRules`). */
export interface CardRules {
  /** Unclaimed days kept to claim later (0: a day not claimed on its own UTC day is lost). */
  readonly bankDays: number;
  /** A running card can be bought again (extending it) when this many days or fewer are left. */
  readonly rebuyWindowDays: number;
}

export interface DailyThemeDef { readonly id: string; readonly name: string; readonly tiers: readonly (readonly OfferItem[])[] }

/** Daily Deals: three priced tiers whose non-gem items come from the day's theme, and Buy All. */
export interface DailyDealsDef {
  readonly tiers: readonly { readonly price: OfferPrice; readonly gems: number }[];
  readonly buyAll: OfferPrice;
  readonly themes: readonly DailyThemeDef[];
}

export interface TrackMilestoneDef { readonly tokens: number; readonly items: readonly OfferItem[] }

export interface OfferPageDef {
  readonly id: string;
  readonly title: string;
  readonly blurb: string;
  /** Art key: `captain:<pose>` (art/interface/captain), `portrait:<id>`, `chest:<rarity>`, `icon:<name>`. */
  readonly art: string;
  readonly rotation: Rotation;
  readonly unlockAfterLevel: number;
  readonly offers: readonly OfferDef[];
  readonly daily: DailyDealsDef | null;
  readonly track: readonly TrackMilestoneDef[];
  /** The gem packs page (shop.json gemPacks). */
  readonly gemPacks: boolean;
  /**
   * MP-09: how the page states value: "percent" (the value burst: gem packs, one-off packs, cards, the pass — the
   * default, so a new page inherits it) or "worth" (a quiet "Worth N gems" line: the recurring deals).
   */
  readonly valueStyle: ValueStyle;
  /** MP-04: the Deals pill is badged while this page is on sale and Deals has not been opened since (a badge, never a pop-up). */
  readonly badgeUntilSeen: boolean;
  /**
   * A price ladder (docs/SHOP.md §18: Custom Chest, Master's Collection, Armoury): its tiers are sold one after another,
   * each on sale once the one before it was bought this period, so the Store shows one pack at a time.
   */
  readonly ladder: boolean;
}

export const VALUE_STYLES = ["percent", "worth"] as const;
export type ValueStyle = (typeof VALUE_STYLES)[number];

export interface LuckyChestDef { readonly id: string; readonly name: string; /** Percent per rarity (index = Rarity), sums to 100. */ readonly weights: readonly number[] }

/** VIP crests (vip.json `crest`; art/interface/icons/vip_crest_<tier>.png). */
export const VIP_CRESTS = ["none", "bronze", "silver", "gold", "diamond"] as const;
export type VipCrest = (typeof VIP_CRESTS)[number];

export interface OfferValueData {
  /** The gem pack (shop.json gemPacks id) whose gems per cent is 100 % value. */
  readonly baselinePack: string;
  /** Per rarity: a gear item's gem price (docs/SHOP.md §1.2). */
  readonly gearGems: readonly number[];
  /** A value starburst shows only at or above this percent. */
  readonly showFromPercent: number;
}

export interface OffersData {
  readonly pages: readonly OfferPageDef[];
  readonly luckyChests: ReadonlyMap<string, LuckyChestDef>;
  readonly value: OfferValueData;
  /** Skin band → gems paid instead of a skin the player already owns. */
  readonly skinRefundGems: ReadonlyMap<string, number>;
  /** App Store storefronts (alpha-3) where no offer holding a random item is shown. */
  readonly noRandomStorefronts: readonly string[];
  readonly cardRules: CardRules;
  /** MP-05: the rules that suggest an offer from the save (badges only; data/offerTriggers.ts). */
  readonly triggers: OfferTriggerRules;
}

/** The card rules a bundle without `cardRules` gets (MP-01 spec: bank 2 days, re-buy in the last 3). */
export const DEFAULT_CARD_RULES: CardRules = { bankDays: 2, rebuyWindowDays: 3 };

/** The page that lists shop.json's gem packs. */
export const GEM_PACKS_PAGE = "gems";
/** The page of VIP Special Packs (one per VIP level, `vipLevel`): shown on the VIP page, never on the Deals tab strip. */
export const VIP_PACKS_PAGE = "vip";
/** The page of builders 2–5 (docs/SHOP.md §18): real money only, one after another. */
export const BUILDERS_PAGE = "builders";

export function emptyOffers(): OffersData {
  return {
    pages: [], luckyChests: new Map(), value: { baselinePack: "", gearGems: RARITY_IDS.map(() => 0), showFromPercent: 0 },
    skinRefundGems: new Map(), noRandomStorefronts: [], cardRules: DEFAULT_CARD_RULES, triggers: NO_OFFER_TRIGGERS,
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Json = any;

const ITEM_KINDS: readonly OfferItemKind[] = ["gems", "supplies", "speedUp", "chest", "luckyChest", "gear", "skin"];
const PRICE = /^\$(\d+)\.(\d\d)$/;
const CENTS_PER_DOLLAR = 100;
const PERCENT = 100;

function int(v: Json, where: string): number {
  if (typeof v === "number" && Number.isInteger(v) && v >= 0) return v;
  throw new TypeError(`${where}: expected a whole number, got ${JSON.stringify(v)}`);
}
function str(v: Json, where: string): string {
  if (typeof v !== "string" || v === "") throw new TypeError(`${where}: expected a string, got ${JSON.stringify(v)}`);
  return v;
}
function rarity(s: Json, where: string): Rarity {
  const i = (RARITY_IDS as readonly string[]).indexOf(s);
  if (i < 0) throw new Error(`${where}: unknown rarity '${s}'`);
  return i as Rarity;
}
function fields(o: Json): string[] { return Object.keys(o ?? {}).filter((k) => !k.startsWith("_")); }

/** "$4.99" → 499 cents; throws on anything that is not a $x.yy tier. */
export function priceCents(label: string): number {
  const m = PRICE.exec(label);
  if (!m) throw new Error(`price '${label}' is not a $x.yy tier`);
  return Number(m[1]) * CENTS_PER_DOLLAR + Number(m[2]);
}

function price(product: Json, label: Json, where: string): OfferPrice {
  try { return { product: str(product, where), label: str(label, where), cents: priceCents(label) }; } catch (e) { throw new Error(`${where}: ${(e as Error).message}`); }
}

/** One offers.json item: exactly one kind key, plus `count` (and `slot` for gear). */
export function parseOfferItem(o: Json, where: string): OfferItem {
  const kinds = fields(o).filter((k) => (ITEM_KINDS as readonly string[]).includes(k));
  if (kinds.length !== 1) throw new Error(`${where}: an item needs exactly one of ${ITEM_KINDS.join("/")}`);
  const kind = kinds[0] as OfferItemKind, v = o[kind];
  const count = o.count === undefined ? 1 : int(o.count, where);
  if (count < 1) throw new Error(`${where}: count must be at least 1`);
  const one = (): 1 => { if (count !== 1) throw new Error(`${where}: ${kind} takes no count`); return 1; };
  switch (kind) {
    case "gems": return { kind, amount: int(v, where), count: one() };
    case "supplies": return { kind, chests: Fix64.parse(str(v, where)), count: one() };
    case "speedUp": return { kind, minutes: int(v, where), count };
    case "chest": return { kind, rarity: rarity(v, where), count };
    case "luckyChest": return { kind, table: str(v, where), count };
    case "gear": {
      const slot = o.slot ?? "any";
      if (slot !== "pick" && slot !== "any") throw new Error(`${where}: gear slot must be pick or any`);
      return { kind, rarity: rarity(v, where), slot, count };
    }
    case "skin": return { kind, skinId: str(v, where), count: one() };
  }
}

const items = (list: Json, where: string): OfferItem[] => ((list ?? []) as Json[]).map((it, i) => parseOfferItem(it, `${where}[${i}]`));

/** The first builder is every Commander's own: an offer hires builder 2 or later. */
const FIRST_SOLD_BUILDER = 2;
function builderNumber(v: Json, where: string): number {
  const n = int(v, where);
  if (n < FIRST_SOLD_BUILDER) throw new Error(`${where}: builder must be ${FIRST_SOLD_BUILDER} or more`);
  return n;
}

function parseOffer(o: Json, pageId: string): OfferDef {
  const id = str(o.id, `page ${pageId} offer`), where = `offer ${id}`;
  return {
    id, pageId, name: str(o.name, where), price: price(o.product, o.price, where), limit: int(o.limit, where),
    items: items(o.items, `${where} items`), picks: o.picks === undefined ? 0 : int(o.picks, where), choices: items(o.choices, `${where} choices`),
    prizes: items(o.prizes, `${where} prizes`), weeklyTokens: o.weeklyTokens === undefined ? 0 : int(o.weeklyTokens, where),
    levelTrigger: o.levelTrigger === undefined ? 0 : int(o.levelTrigger, where),
    vipLevel: o.vipLevel === undefined ? 0 : int(o.vipLevel, where),
    after: o.after === undefined ? "" : str(o.after, where), welcomeBack: o.welcomeBack === true,
    card: o.card === undefined ? null : { days: int(o.card.days, `${where} card`), daily: items(o.card.daily, `${where} card daily`) },
    fund: o.fund === undefined ? null : { milestones: ((o.fund.milestones ?? []) as Json[]).map((m, i) => ({ level: int(m.level, `${where} fund ${i}`), items: items(m.items, `${where} fund ${i}`) })) },
    builder: o.builder === undefined ? 0 : builderNumber(o.builder, where),
  };
}

function parseDaily(p: Json): DailyDealsDef | null {
  if (p.tiers === undefined) return null;
  const tiers = (p.tiers as Json[]).map((t, i) => ({ price: price(t.product, t.price, `daily tier ${i + 1}`), gems: int(t.gems, `daily tier ${i + 1}`) }));
  const themes = (p.themes as Json[]).map((t): DailyThemeDef => {
    const where = `daily theme ${t.id}`;
    const tt = (t.tiers as Json[]).map((list, i) => items(list, `${where} tier ${i + 1}`));
    if (tt.length !== tiers.length) throw new Error(`${where}: needs ${tiers.length} tiers`);
    return { id: str(t.id, where), name: str(t.name, where), tiers: tt };
  });
  return { tiers, buyAll: price(p.buyAll.product, p.buyAll.price, "daily buyAll"), themes };
}

function valueStyle(v: Json, where: string): ValueStyle {
  if (v === undefined) return "percent";
  if (!(VALUE_STYLES as readonly string[]).includes(v)) throw new Error(`${where}: valueStyle must be ${VALUE_STYLES.join(" or ")}`);
  return v as ValueStyle;
}

/** Reads the bundle's `offers` section (gen.py has validated it; this re-checks shape and throws on anything else). */
export function parseOffers(root: Json): OffersData {
  const s = root?.offers;
  if (s === undefined) return emptyOffers();
  const luckyChests = new Map(Object.entries(s.luckyChests as Record<string, Json>).map(([id, c]): [string, LuckyChestDef] => {
    const weights = RARITY_IDS.map((r) => (c.weights[r] === undefined ? 0 : int(c.weights[r], `lucky chest ${id}`)));
    if (weights.reduce((a, b) => a + b, 0) !== PERCENT) throw new Error(`lucky chest ${id}: weights must sum to ${PERCENT}`);
    return [id, { id, name: str(c.name, `lucky chest ${id}`), weights }];
  }));
  const pages = (s.pages as Json[]).map((p): OfferPageDef => {
    const id = str(p.id, "page"), rotation = p.rotation as Rotation;
    if (!ROTATIONS.includes(rotation)) throw new Error(`page ${id}: unknown rotation '${p.rotation}'`);
    return {
      id, title: str(p.title, `page ${id}`), blurb: str(p.blurb, `page ${id}`), art: str(p.art, `page ${id}`), rotation,
      unlockAfterLevel: int(p.unlockAfterLevel, `page ${id}`), offers: ((p.offers ?? []) as Json[]).map((o) => parseOffer(o, id)),
      daily: parseDaily(p), track: ((p.track ?? []) as Json[]).map((m, i) => ({ tokens: int(m.tokens, `page ${id} track ${i}`), items: items(m.items, `page ${id} track ${i}`) })),
      gemPacks: id === GEM_PACKS_PAGE, valueStyle: valueStyle(p.valueStyle, `page ${id}`), badgeUntilSeen: p.badgeUntilSeen === true,
      ladder: p.ladder === true,
    };
  });
  const v = s.value;
  return {
    pages, luckyChests,
    value: { baselinePack: str(v.baselinePack, "value"), gearGems: RARITY_IDS.map((r) => int(v.gearGems[r], `value gearGems ${r}`)), showFromPercent: int(v.showFromPercent, "value") },
    skinRefundGems: new Map(Object.entries(s.skinRefundGems as Record<string, Json>).map(([k, g]) => [k, int(g, `skinRefundGems ${k}`)])),
    noRandomStorefronts: ((s.noRandomStorefronts ?? []) as Json[]).map((c) => str(c, "noRandomStorefronts")),
    cardRules: s.cardRules === undefined ? DEFAULT_CARD_RULES
      : { bankDays: int(s.cardRules.bankDays, "cardRules"), rebuyWindowDays: int(s.cardRules.rebuyWindowDays, "cardRules") },
    triggers: parseOfferTriggers(s.triggers),
  };
}
